import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const alreadyPaid = params.get('already_paid');
  const invNo = params.get('inv');
  const [state, setState] = useState(alreadyPaid ? 'already_paid' : 'checking');
  const [invoiceNumber, setInvoiceNumber] = useState(invNo || '');

  useEffect(() => {
    if (!sessionId || alreadyPaid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        setInvoiceNumber(res.data.invoice_number || '');
        if (res.data.payment_status === 'paid') return setState('paid');
        if (['failed', 'expired'].includes(res.data.payment_status)) return setState('failed');
      } catch (e) { /* keep polling */ }
      if (attempts < 10) setTimeout(poll, 2000);
      else setState('timeout');
    };
    poll();
  }, [sessionId, alreadyPaid]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow-lg p-10 max-w-md w-full text-center" data-testid="payment-result-card">
        {state === 'checking' && (
          <>
            <Loader2 className="mx-auto animate-spin text-green-600 mb-4" size={48} />
            <h1 className="text-2xl font-bold text-gray-900">Confirming your payment…</h1>
            <p className="text-gray-500 mt-2">Please wait a moment — do not close this page.</p>
          </>
        )}
        {(state === 'paid' || state === 'already_paid') && (
          <>
            <CheckCircle className="mx-auto text-green-600 mb-4" size={56} />
            <h1 className="text-2xl font-bold text-gray-900">
              {state === 'already_paid' ? 'Invoice Already Paid' : 'Payment Successful!'}
            </h1>
            <p className="text-gray-600 mt-2">
              {invoiceNumber ? <>Invoice <strong>{invoiceNumber}</strong> </> : 'Your invoice '}
              {state === 'already_paid' ? 'has already been settled.' : 'has been marked as PAID.'}
            </p>
            <p className="text-gray-500 mt-4 text-sm">Thank you for your business!<br />NJK Logistics Pte Ltd</p>
          </>
        )}
        {(state === 'failed' || state === 'timeout') && (
          <>
            <XCircle className="mx-auto text-red-500 mb-4" size={56} />
            <h1 className="text-2xl font-bold text-gray-900">
              {state === 'failed' ? 'Payment Not Completed' : 'Still Confirming…'}
            </h1>
            <p className="text-gray-600 mt-2">
              {state === 'failed'
                ? 'The payment did not go through. You can try again from the link in your invoice.'
                : "Your payment is taking longer than usual to confirm. If you were charged, we'll mark the invoice paid automatically — no action needed."}
            </p>
          </>
        )}
        <Link to="/" className="inline-block mt-6 text-green-700 font-medium hover:underline">Back to NJK Logistics</Link>
      </div>
    </div>
  );
}
